import { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useBrandingOrDefault } from '@/contexts/BrandingContext'
import {
  LayoutDashboard,
  Settings,
  Bell,
  User,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Shield,
  CreditCard,
  Users,
  DollarSign,
  ShoppingCart,
  Key,
  Webhook,
  Database,
  Trash2,
  CheckCircle2,
  RefreshCw,
  Loader2,
  FileText,
  ShieldCheck,
  Plus,
  Building,
  TrendingUp,
  Receipt,
  Banknote,
  Calculator,
  Globe,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Notification } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/utils/cn'
import { getBrandingPreviewStyles } from '@/utils/branding'

interface DashboardLayoutProps {
  children: ReactNode
}

interface NavItem {
  title: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  children?: NavItem[]
  roles?: string[]
  badge?: string
  badgeColor?: string
}

// Role-based navigation configuration
const getNavigation = (userRole: string, isSuperuser?: boolean, isStaff?: boolean): NavItem[] => {
  const baseNavigation: NavItem[] = [
    {
      title: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
    },
  ]

  // Merchant-specific navigation
  if (userRole === 'merchant' || userRole === 'admin' || userRole === 'merchant_admin' || userRole === 'merchant_user') {
    baseNavigation.push(
      {
        title: 'Transactions',
        href: '/transactions',
        icon: CreditCard,
      },
      // {
      //   title: 'Payment Links',
      //   href: '/payment-links',
      //   icon: LinkIcon,
      // },
      {
        title: 'Sales Channels',
        href: '/sale-shops',
        icon: ShoppingCart,
      },
      {
        title: 'Customers',
        href: '/customers',
        icon: Users,
      },
      {
        title: 'Documents',
        href: '/documents',
        icon: FileText,
      }
    )
  }

  // Admin-specific navigation
  if (userRole === 'admin' || userRole === 'superuser' || isSuperuser || isStaff) {
    baseNavigation.push({
      title: 'Admin',
      icon: Shield,
      children: [
        {
          title: 'Merchants',
          href: '/admin/merchants',
          icon: Building,
        },
        {
          title: 'Create Merchant',
          href: '/admin/merchants/create',
          icon: Plus,
        },
        {
          title: 'Sales Channels',
          href: '/admin/sale-channels',
          icon: ShoppingCart,
        },
        {
          title: 'Create Channel',
          href: '/admin/sale-channels/create',
          icon: Plus,
        },
        {
          title: 'User Management',
          href: '/admin/users',
          icon: Users,
        },
        {
          title: 'Create User',
          href: '/admin/users/create',
          icon: User,
        },
        {
          title: 'Document Management',
          href: '/admin/documents',
          icon: ShieldCheck,
        },
        {
          title: 'Payout Management',
          href: '/admin/payouts',
          icon: TrendingUp,
        },
        {
          title: 'Payout Batches',
          href: '/admin/payout-batches',
          icon: Receipt,
        },
        {
          title: 'Settlements',
          href: '/admin/settlements',
          icon: Banknote,
        },
        {
          title: 'Commission Records',
          href: '/admin/commission-records',
          icon: Calculator,
        },
        {
          title: 'System',
          href: '/system',
          icon: Database,
        },
        {
          title: 'White Label Domains',
          href: '/admin/white-label-domains',
          icon: Globe,
        },
      ],
    })
  }

  // Merchant Admin-specific navigation (for managing their own merchant)
  if (userRole === 'merchant_admin') {
    baseNavigation.push({
      title: 'Merchant Management',
      icon: Building,
      children: [
        {
          title: 'Merchant Profile',
          href: '/merchant/details',
          icon: Building,
        },
        {
          title: 'Merchant Users',
          href: '/merchant/users',
          icon: Users,
        },
        {
          title: 'Create User',
          href: '/merchant/users/create',
          icon: User,
        },
        {
          title: 'White Label Domains',
          href: '/merchant/white-label-domains',
          icon: Globe,
        },
      ],
    })
  }

  // Common navigation for all roles
  baseNavigation.push({
    title: 'Settings',
    href: '/settings',
    icon: Settings,
  })

  return baseNavigation
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState<string[]>(['Admin'])
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false)
  const { user, logout, switchEnvironment } = useAuthStore()
  const { branding } = useBrandingOrDefault()
  const [selectedEnvironment, setSelectedEnvironment] = useState<'production' | 'sandbox'>(
    user?.environment ?? 'sandbox'
  )
  const [pendingEnvironment, setPendingEnvironment] = useState<'production' | 'sandbox' | null>(
    null
  )
  const {
    notifications,
    unreadCount,
    isLoading: notificationsLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationStore()
  const location = useLocation()

  useEffect(() => {
    setSelectedEnvironment(user?.environment ?? 'sandbox')
  }, [user?.environment])

  const environmentOptions: { label: string; value: 'production' | 'sandbox' }[] = [
    { label: 'Sandbox', value: 'sandbox' },
    { label: 'Production', value: 'production' },
  ]

  const handleEnvironmentChange = async (environment: 'production' | 'sandbox') => {
    if (!user || environment === user.environment || pendingEnvironment) {
      return
    }

    setPendingEnvironment(environment)
    setSelectedEnvironment(environment)

    try {
      await switchEnvironment(environment)
    } catch (error) {
      setSelectedEnvironment(user?.environment ?? 'sandbox')
    } finally {
      setPendingEnvironment(null)
    }
  }

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  // Get role-based navigation
  const navigation = getNavigation(
    user?.role || 'user',
    user?.is_superuser,
    user?.is_staff
  )

  // Debug: Log user info to help troubleshoot navigation visibility
  useEffect(() => {
    if (user) {

    }
  }, [user])

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    const { isAuthenticated } = useAuthStore.getState()
    if (user && isAuthenticated) {
      fetchNotifications({ page: 1, limit: 10 })
      fetchUnreadCount()

      // Set up polling for real-time updates
      const interval = setInterval(() => {
        const { isAuthenticated: currentAuth } = useAuthStore.getState()
        if (currentAuth) {
          fetchUnreadCount()
          fetchNotifications({ page: 1, limit: 10, refresh: true })
        }
      }, 30000) // Poll every 30 seconds

      return () => clearInterval(interval)
    }
  }, [user, fetchNotifications, fetchUnreadCount])

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title) ? prev.filter(item => item !== title) : [...prev, title]
    )
  }

  const isActive = (href: string) => {
    return location.pathname === href
  }

  // Notification handlers
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }
    if (notification.action_url) {
      window.open(notification.action_url, '_blank')
    }
    setNotificationDropdownOpen(false)
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const handleDeleteNotification = async (notificationId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    await deleteNotification(notificationId)
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'transaction':
        return CreditCard
      case 'payment':
        return DollarSign
      case 'account':
        return User
      case 'system':
        return Database
      case 'security':
        return Shield
      case 'webhook':
        return Webhook
      case 'api':
        return Key
      default:
        return Bell
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-orange-600 bg-orange-50 border-orange-200'
      case 'high':
        return 'text-orange-700 bg-orange-100 border-orange-300'
      case 'normal':
        return 'text-blue-600 bg-blue-50 border-blue-200'
      case 'low':
        return 'text-gray-600 bg-gray-50 border-gray-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const renderNavItem = (item: NavItem, level = 0) => {
    const isExpanded = expandedItems.includes(item.title)
    const hasChildren = item.children && item.children.length > 0

    if (hasChildren) {
      return (
        <Collapsible
          key={item.title}
          open={isExpanded}
          onOpenChange={() => toggleExpanded(item.title)}
        >
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full justify-start h-12 px-4 rounded-xl transition-all duration-200 ${level > 0 ? 'ml-4' : ''} ${item.children?.some(child => isActive(child.href || ''))
                ? 'text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                : 'hover:text-white hover:shadow-md'
                }`}
              style={item.children?.some(child => isActive(child.href || '')) ? {
                background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
                color: branding.textColor
              } : {
                color: branding.textSecondaryColor
              }}
            >
              <item.icon className="mr-3 h-5 w-5" />
              <span className="flex-1 text-left font-medium">{item.title}</span>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-1 mt-2">
            {item.children?.map(child => (
              <Button
                key={child.title}
                variant="ghost"
                asChild
                className={`w-full justify-start h-10 px-4 ml-4 rounded-lg transition-all duration-200 ${isActive(child.href || '')
                  ? 'text-white shadow-md hover:shadow-lg'
                  : 'hover:text-white'
                  }`}
                style={isActive(child.href || '') ? {
                  background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
                  color: branding.textColor
                } : {
                  color: branding.textSecondaryColor
                }}
              >
                <Link to={child.href || '#'}>
                  <child.icon className="mr-3 h-4 w-4" />
                  {child.title}
                </Link>
              </Button>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )
    }

    return (
      <Button
        key={item.title}
        variant="ghost"
        asChild
        className={`w-full justify-start h-12 px-4 rounded-xl transition-all duration-200 ${level > 0 ? 'ml-4' : ''} ${isActive(item.href || '')
          ? 'text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
          : 'hover:text-white hover:shadow-md'
          }`}
        style={isActive(item.href || '') ? {
          background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
          color: branding.textColor
        } : {
          color: branding.textSecondaryColor
        }}
      >
        <Link to={item.href || '#'}>
          <item.icon className="mr-3 h-5 w-5" />
          <span className="font-medium">{item.title}</span>
        </Link>
      </Button>
    )
  }

  // Get branding styles for dynamic theming
  const brandingStyles = getBrandingPreviewStyles(branding)

  return (
    <div
      className="relative flex flex-col min-h-screen overflow-hidden scroll-smooth"
      style={{
        backgroundColor: branding.backgroundColor,
        ...brandingStyles
      }}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute rounded-full -top-40 -right-40 w-80 h-80 blur-3xl animate-pulse-slow"
          style={{
            background: `linear-gradient(135deg, ${branding.primaryColor}20 0%, ${branding.secondaryColor}20 100%)`
          }}
        ></div>
        <div
          className="absolute rounded-full -bottom-40 -left-40 w-80 h-80 blur-3xl animate-pulse-slow"
          style={{
            animationDelay: '1s',
            background: `linear-gradient(135deg, ${branding.secondaryColor}20 0%, ${branding.primaryColor}20 100%)`
          }}
        ></div>
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-96 h-96 blur-3xl animate-float"
          style={{
            background: `linear-gradient(135deg, ${branding.primaryColor}15 0%, ${branding.secondaryColor}15 100%)`
          }}
        ></div>
      </div>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 backdrop-blur-md border-r border-white/20 shadow-2xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        style={{
          backgroundColor: `${branding.surfaceColor}95`,
        }}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b border-white/20 h-20 backdrop-blur-md"
            style={{
              backgroundColor: `${branding.surfaceColor}95`,
            }}
          >
            <Link to="/dashboard" className="flex items-center space-x-3 group">
              {branding.brandName ? (
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  {branding.brandName.split(' ').length > 1
                    ? branding.brandName.split(' ').slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
                    : branding.brandName.charAt(0).toUpperCase()
                  }
                </div>
              ) : (
                <img src="/icon.svg" alt="Icon" className="h-8 w-8" />
              )}
              <span
                className="text-xl font-bold"
                style={{ color: branding.textColor }}
              >
                {branding.brandName}
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden hover:bg-white/20 rounded-xl"
              style={{ color: branding.textColor }}
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-6 space-y-3 overflow-y-auto">
            <div className="space-y-1">{navigation.map(item => renderNavItem(item))}</div>
          </nav>

          {/* User info */}
          <div
            className="p-6 border-t border-white/20 backdrop-blur-md"
            style={{
              backgroundColor: `${branding.surfaceColor}95`,
            }}
          >
            <div className="flex items-center space-x-4">
              <Avatar className="h-12 w-12 ring-2 shadow-lg">
                <AvatarImage src={user?.author_image_url || user?.first_name || ''} alt={user?.first_name || ''} />
                <AvatarFallback
                  className="text-sm font-semibold text-white"
                  style={{
                    background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`
                  }}
                >
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-semibold truncate"
                  style={{ color: branding.textColor }}
                >
                  {user?.first_name} {user?.last_name}
                </p>
                <p
                  className="text-xs truncate"
                  style={{ color: branding.textSecondaryColor }}
                >
                  {user?.email}
                </p>
                <div className="flex items-center mt-1">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                    style={{
                      backgroundColor: `${branding.primaryColor}20`,
                      color: branding.primaryColor,
                      borderColor: `${branding.primaryColor}30`
                    }}
                  >
                    {user?.role}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header
          className="sticky top-0 z-30 backdrop-blur-md border-b border-white/20 h-20 shadow-sm"
          style={{
            backgroundColor: `${branding.surfaceColor}95`,
          }}
        >
          <div className="flex items-center justify-between px-6 py-4 h-full">
            <div className="flex items-center space-x-6">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden hover:bg-white/20 rounded-xl"
                style={{ color: branding.textColor }}
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <div
                  className="p-2 rounded-lg text-white"
                  style={{
                    background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`
                  }}
                >
                  {(() => {
                    const currentNavItem = navigation.find(
                      item =>
                        item.href === location.pathname ||
                        item.children?.some(child => child.href === location.pathname)
                    )
                    const IconComponent = currentNavItem?.icon
                    return IconComponent ? <IconComponent className="h-5 w-5 text-white" /> : null
                  })()}
                </div>
                <div>
                  <h1
                    className="text-xl font-bold"
                    style={{ color: branding.textColor }}
                  >
                    {navigation.find(
                      item =>
                        item.href === location.pathname ||
                        item.children?.some(child => child.href === location.pathname)
                    )?.title || 'Dashboard'}
                  </h1>
                  <p
                    className="text-sm -mt-1"
                    style={{ color: branding.textSecondaryColor }}
                  >
                    {location.pathname === '/dashboard'
                      ? 'Payment Analytics & Overview'
                      : 'Payment Management & Control'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex flex-col items-start space-y-1">
                <span
                  className="text-[10px] uppercase tracking-[0.2em]"
                  style={{ color: branding.textSecondaryColor }}
                >
                  Environment
                </span>
                <div className="flex items-center rounded-2xl border border-white/20 bg-white/10 p-0.5 shadow-inner">
                  {environmentOptions.map(option => {
                    const isSelected = selectedEnvironment === option.value
                    const isPending = pendingEnvironment === option.value
                    return (
                      <Button
                        key={option.value}
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEnvironmentChange(option.value)}
                        disabled={isSelected || pendingEnvironment !== null}
                        className={cn(
                          'h-8 px-3 text-xs font-semibold tracking-wide uppercase transition-all duration-200 rounded-xl',
                          isSelected
                            ? 'text-white shadow-lg'
                            : 'hover:text-white hover:bg-white/10'
                        )}
                        style={isSelected ? {
                          background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
                          color: branding.textColor
                        } : {
                          color: branding.textSecondaryColor
                        }}
                      >
                        <span className="flex items-center">
                          {option.label}
                          {isPending && <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" />}
                        </span>
                      </Button>
                    )
                  })}
                </div>
              </div>
              {/* Notifications */}
              <DropdownMenu
                open={notificationDropdownOpen}
                onOpenChange={setNotificationDropdownOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-10 w-10 rounded-xl hover:bg-white/20 transition-all duration-200"
                    style={{ color: branding.textColor }}
                  >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span
                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center border-2 border-white shadow-lg animate-pulse"
                        style={{ backgroundColor: branding.primaryColor }}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-96 max-h-[500px] overflow-y-auto shadow-2xl border-0 bg-gray-900 backdrop-blur-md"
                  align="end"
                >
                  <DropdownMenuLabel className="flex items-center justify-between p-4 bg-gradient-to-r from-pexi-orange/10 to-pexi-blue/10 border-b border-gray-200/50">
                    <div className="flex items-center space-x-2">
                      <Bell className="h-5 w-5 text-pexi-orange" />
                      <span className="font-semibold text-gray-900">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-pexi-orange text-white">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {unreadCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleMarkAllAsRead}
                          className="h-7 px-3 text-xs font-medium text-pexi-orange hover:text-pexi-orange-dark hover:bg-pexi-orange/10 rounded-lg"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Mark all read
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchNotifications({ page: 1, limit: 10, refresh: true })}
                        className="h-7 px-3 text-xs font-medium text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        disabled={notificationsLoading}
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1 ${notificationsLoading ? 'animate-spin' : ''}`}
                        />
                        Refresh
                      </Button>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  {notificationsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-gray-500">Loading notifications...</span>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Bell className="h-8 w-8 text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2 p-2">
                      {notifications.map((notification: Notification) => {
                        const IconComponent = getNotificationIcon(notification.notification_type)
                        const priorityColor = getPriorityColor(notification.priority)

                        return (
                          <div
                            key={notification.id}
                            className={`p-4 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${!notification.is_read
                              ? 'bg-gradient-to-r from-pexi-orange/10 to-pexi-orange/20 border-l-4 border-pexi-orange shadow-sm'
                              : 'hover:bg-gray-50'
                              }`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex items-start space-x-4">
                              <div className={`p-2 rounded-xl shadow-sm ${priorityColor}`}>
                                <IconComponent className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <p
                                    className={`text-sm font-semibold ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}
                                  >
                                    {notification.title}
                                  </p>
                                  <div className="flex items-center space-x-2">
                                    {notification.priority === 'urgent' && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-pexi-orange text-white animate-pulse">
                                        Urgent
                                      </span>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={e => handleDeleteNotification(notification.id, e)}
                                      className="h-7 w-7 p-0 text-gray-400 hover:text-pexi-orange hover:bg-pexi-orange/10 rounded-lg"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                <p
                                  className="text-sm text-gray-600 mb-3 overflow-hidden text-ellipsis"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {notification.message}
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500 font-medium">
                                    {formatDistanceToNow(new Date(notification.created_at), {
                                      addSuffix: true,
                                    })}
                                  </span>
                                  <span
                                    className={`text-xs px-3 py-1 rounded-full font-medium ${priorityColor}`}
                                  >
                                    {notification.priority}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {notifications.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <div className="p-2">
                        <Button
                          variant="ghost"
                          className="w-full justify-center text-sm"
                          onClick={() => {
                            setNotificationDropdownOpen(false)
                            // Navigate to notifications page
                            window.location.href = '/notifications'
                          }}
                        >
                          View all notifications
                        </Button>
                      </div>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-11 w-auto px-3 py-2 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-7 w-7 ring-2">
                        <AvatarImage src={user?.author_image_url || user?.first_name || ''} alt={user?.first_name || ''} />
                        <AvatarFallback
                          className="text-xs font-semibold text-white"
                          style={{ backgroundColor: branding.primaryColor }}
                          title={`${user?.first_name} ${user?.last_name}`}
                        >
                          {user?.first_name?.[0]}
                          {user?.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden sm:block text-left">
                        <p
                          className="text-sm font-medium"
                          style={{ color: branding.textColor }}
                        >
                          {user?.first_name}
                        </p>
                        <p
                          className="text-xs"
                          style={{ color: branding.textSecondaryColor }}
                        >
                          {user?.role}
                        </p>
                      </div>
                      <ChevronDown
                        className="h-4 w-4"
                        style={{ color: branding.textSecondaryColor }}
                      />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-64 shadow-2xl border-0 backdrop-blur-md"
                  style={{ backgroundColor: `${branding.surfaceColor}95` }}
                  align="end"
                  forceMount
                >
                  <DropdownMenuLabel
                    className="font-normal p-4"
                    style={{
                      background: `linear-gradient(135deg, ${branding.primaryColor}10 0%, ${branding.secondaryColor}10 100%)`
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-10 w-10 ring-2">
                        <AvatarImage src={user?.author_image_url || user?.first_name || ''} alt={user?.first_name || ''} />
                        <AvatarFallback
                          className="text-sm font-bold text-white"
                          style={{ backgroundColor: branding.primaryColor }}
                        >
                          {user?.first_name?.[0]}
                          {user?.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col space-y-1">
                        <p
                          className="text-sm font-semibold leading-none"
                          style={{ color: branding.textColor }}
                        >
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p
                          className="text-xs leading-none"
                          style={{ color: branding.textSecondaryColor }}
                        >
                          {user?.email}
                        </p>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit"
                          style={{
                            backgroundColor: `${branding.primaryColor}20`,
                            color: branding.primaryColor
                          }}
                        >
                          {user?.role}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="p-2 space-y-1">
                    <DropdownMenuItem
                      asChild
                      className="rounded-lg hover:bg-opacity-10"
                    >
                      <Link to="/settings/profile" className="flex items-center px-3 py-2">
                        <User
                          className="mr-3 h-4 w-4"
                          style={{ color: branding.textSecondaryColor }}
                        />
                        <span
                          className="font-medium"
                          style={{ color: branding.textColor }}
                        >
                          Profile
                        </span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      asChild
                      className="rounded-lg hover:bg-opacity-10"
                    >
                      <Link to="/settings" className="flex items-center px-3 py-2">
                        <Settings
                          className="mr-3 h-4 w-4"
                          style={{ color: branding.textSecondaryColor }}
                        />
                        <span
                          className="font-medium"
                          style={{ color: branding.textColor }}
                        >
                          Settings
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  </div>
                  <DropdownMenuSeparator />
                  <div className="p-2">
                    <DropdownMenuItem
                      onClick={logout}
                      className="rounded-lg hover:bg-opacity-10"
                      style={{ color: branding.primaryColor }}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      <span className="font-medium">Log out</span>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 relative z-10">{children}</main>
      </div>
    </div>
  )
}
